/**
 * Клиент маршрутов прав — Блок 2 · Ф8 «Права доступа» · В-Э.
 *
 *   GET /users/:id/rights          — карточка: восемь строк по одному человеку;
 *   PUT /users/:id/rights/:right   — добавить одно право либо сбросить строку
 *                                    (запрет сервер не ставит никому — 11.10.2026);
 *   GET /user-rights               — сводный список.
 *
 * Все три на сервере — только штатному супервайзеру; вызывающий обязан сам
 * решить, монтировать ли запрос (`canManageRights`), иначе администратор
 * получит 403 на каждом открытии карточки.
 *
 * ─── Договор записи (отчёт В-О1б, разделы 11–12) ────────────────────────────
 *
 * 1. `version` строки уходит в запрос как есть; у права без строки
 *    (`version: null`) поле НЕ присылается — несовпадение в любую сторону
 *    сервер считает конфликтом.
 * 2. Два разных 409, и путать их нельзя:
 *      · `VERSION_CONFLICT` — «перечитайте»: строку изменили, пока её смотрели.
 *        Повторять тот же запрос бессмысленно — версия устарела;
 *      · `serialization_conflict` — «повторите»: сервер исчерпал свои пять
 *        попыток под одновременной записью. Тот же запрос пройдёт вторым разом.
 *    Первое возвращается вызывающему, второе повторяется здесь — ограниченно.
 * 3. Запись — по одной: вызывающий не запускает вторую, пока не вернулась
 *    первая (блок держит это флагом и замком).
 *
 * ─── Кэш ────────────────────────────────────────────────────────────────────
 *
 * Ответ `PUT` — карточка получателя целиком (правка «видеть» меняет строку
 * парного «вести»): она кладётся в кэш сразу. Затем сбрасывается ВЕСЬ префикс
 * `user-rights` — сводный список и карточки других людей: запрет, поставленный
 * ГИПу, меняет карточки его помощников (Р-197).
 *
 * ⚠️ Профиль получателя (`['auth', 'session']`) не трогается: это чужая сессия.
 * Получатель увидит новое при своём следующем переходе (Р-198; решение плана
 * П14). Свой профиль супервайзера прав не несёт (Р-191).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  RightEffect,
  SetUserRightRequest,
  UserRight,
  UserRightsListResponse,
  UserRightsResponse,
} from '@aris/shared';

import { ApiError, apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

/** Сколько раз один запрос уходит на сервер при `serialization_conflict`. */
export const SET_RIGHT_MAX_ATTEMPTS = 3;
/** Пауза перед повтором, мс × номер попытки. */
export const SET_RIGHT_RETRY_DELAY_MS = 120;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** «Повторите»: сервер не смог записать из-за одновременных изменений. */
export function isSerializationConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'serialization_conflict';
}

/** «Перечитайте»: версия строки, с которой пришёл запрос, устарела. */
export function isVersionConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'VERSION_CONFLICT';
}

// ─── Чтение ──────────────────────────────────────────────────────────────────

/**
 * Карточка прав человека.
 *
 * `staleTime: 0` — явно, мимо порога клиента в 30 с: права человека меняются и
 * не этим экраном (смена должности в той же карточке, повторное приглашение),
 * и блок, смонтированный заново, обязан перечитать, а не показать прежнее.
 */
export function useUserRightsQuery(userId: string | null | undefined, enabled = true) {
  const id = typeof userId === 'string' && userId.length > 0 ? userId : null;
  return useQuery<UserRightsResponse>({
    queryKey: id !== null ? qk.userRights.card(id) : qk.userRights.card('__pending__'),
    queryFn: () => apiFetch<UserRightsResponse>(`/users/${id}/rights`),
    enabled: enabled && id !== null,
    staleTime: 0,
  });
}

/** Сводный список: у кого что добавлено (плюс лежащие запреты прежних времён). */
export function useUserRightsListQuery(enabled = true) {
  return useQuery<UserRightsListResponse>({
    queryKey: qk.userRights.list(),
    queryFn: () => apiFetch<UserRightsListResponse>('/user-rights'),
    enabled,
    staleTime: 0,
  });
}

// ─── Запись ──────────────────────────────────────────────────────────────────

export interface SetUserRightVars {
  userId: string;
  right: UserRight;
  effect: RightEffect;
  /** Версия строки из карточки; `null` — строки нет, поле не присылается. */
  version: number | null;
}

async function putRight(vars: SetUserRightVars): Promise<UserRightsResponse> {
  const body: SetUserRightRequest =
    vars.version === null
      ? { effect: vars.effect }
      : { effect: vars.effect, version: vars.version };
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await apiFetch<UserRightsResponse>(`/users/${vars.userId}/rights/${vars.right}`, {
        method: 'PUT',
        body,
        // Отказ показывает блок — в строке, которую нажали. Всплывающее
        // сообщение фетчера («Нет связи», 429) поверх него было бы вторым
        // сообщением об одном и том же (фикс-пак 1).
        background: true,
      });
    } catch (err) {
      if (!isSerializationConflict(err) || attempt >= SET_RIGHT_MAX_ATTEMPTS) throw err;
      await delay(SET_RIGHT_RETRY_DELAY_MS * attempt);
    }
  }
}

export function useSetUserRightMutation() {
  const qc = useQueryClient();
  return useMutation<UserRightsResponse, unknown, SetUserRightVars>({
    mutationFn: putRight,
    onSuccess: (fresh, vars) => {
      qc.setQueryData(qk.userRights.card(vars.userId), fresh);
      void qc.invalidateQueries({ queryKey: qk.userRights.all });
    },
  });
}
